<template>
  <component
    class="Slider"
    :is="component"
    @mousedown="handleRootMouseDown"
  >
    <span
      ref="rail"
      class="rail"
    />
    <span
      class="track"
      :style="trackStyle"
      />
    <button
      ref="thumb"
      :class="thumbClass"
      :style="thumbStyle"
      @keydown="handleThumbKeyDown"
      @mouseleave="handleThumbMouseLeave"
    >
      <span class="thumb-label">
        <slot name="thumb-label-value" :value="value">
          {{ value }}
        </slot>
      </span>
    </button>
  </component>
</template>

<script>

/*
 * contains a value withing two [ min, max ] boundaries meaning that,
 * should the value be outside the boundary the returned value is equal
 * to the closest boundary
 */
function clamp(value, min, max) {
  return Math.min(Math.max(min, value), max)
}

/*
 * returns a percentage ratio between a number representative of
 * a fraction and a number representative of an entirety
 */
function percent (partial, full) {
  return partial / full * 100
}

/*
 * rounds a number to the closest multiple of another number
 */
function multipleOf (multipleOfNumber) {
  return function (number) {
    return Math.round(number / multipleOfNumber) * multipleOfNumber
  }
}

export default {
  name: 'Slider',

  props: {
    component: {
      type: String,
      default: 'div'
    },

    min: {
      type: Number,
      default: 0
    },

    max: {
      type: Number,
      default: 100
    },

    step: {
      type: Number,
      default: 1
    },

    value: {
      type: Number,
      default: function () {
        return this.min
      }
    }
  },

  data () {
    return {
      dragging: false
    }
  },

  methods: {
    handleRootMouseDown (e) {
      this.$emit('mousedown', e)

      this.dragging = true

      const value = this.getValueFromMouseEvent(e)

      this.emitInput(value)

      document.addEventListener('mousemove', this.handleMouseMove)

      document.addEventListener('mouseup', this.handleDragEnd)
    },

    handleThumbMouseLeave (e) {
      this.$refs.thumb.blur()
    },

    handleThumbKeyDown (e) {
      const valueFromKeydown = this.getValueFromKeydownEvent(e)

      /*
       * If the keydown-event was one which doesn't trigger
       * a change in value 'valueFromKeyboard' will be undefined,
       * however, it can be 0, so a simple falsy-check fails.
       * Therefore, isNaN.
       */
      if (isNaN(valueFromKeydown)) { return }

      const nextValue = this.getValueAsMultipleOfStep(
        clamp(
          valueFromKeydown,
          this.min,
          this.max
        )
      )

      this.emitCommit(nextValue)
    },

    handleMouseMove (e) {
      this.maybeEmitInput(
        this.getValueFromMouseEvent(e)
      )
    },

    handleDragEnd (e) {
      this.dragging = false

      document.removeEventListener('mousemove', this.handleMouseMove)

      document.removeEventListener('mouseup', this.handleDragEnd)

      this.emitCommit(this.value)
    },

    maybeEmitInput (potentialInputValue) {
      const asMultipleOfStep = this.getValueAsMultipleOfStep(potentialInputValue)

      /*
       * only emit if the new value is significantly different to the current value
       * in respect to the new value's form as a multiple of the step interval
       */
      if (asMultipleOfStep !== this.value) {
        this.emitInput(asMultipleOfStep)
      }
    },

    getValueFromKeydownEvent (e) {
      const { key } = e

      switch (key) {
        case 'ArrowRight': { return this.value + this.step }

        case 'ArrowLeft': { return this.value - this.step }

        case 'ArrowUp': {
          /* scroll prevention */
          e.preventDefault()

          return this.value + (this.step * 10)
        }

        case 'ArrowDown': {
          /* scroll prevention */
          e.preventDefault()

          return this.value - (this.step * 10)
        }

        default: return
      }
    },

    getValueFromMouseEvent (mouseEvent) {
      const rail = this.$refs.rail.getBoundingClientRect()

      const offsetRelativeToRailStart = mouseEvent.clientX - rail.x

      const absoluteOffset = clamp(offsetRelativeToRailStart, 0, rail.width)

      const onePercentValue = (this.max - this.min) / 100

      return this.getValueAsMultipleOfStep(
        (onePercentValue * percent(absoluteOffset, rail.width)) + this.min
      )
    },

    getValueAsMultipleOfStep (value) {
      return multipleOf(this.step)(value)
    },

    emitStart (value) {
      this.$emit('start', value)
      this.$emit('input', value)
    },

    emitInput (value) {
      this.$emit('input', value)
    },

    emitCommit (value) {
      /* TODO: rename commit to change and remove change when refactored throughout the codebase */
      this.$emit('commit', value)
      this.$emit('change', value)
    }
  },

  computed: {
    percentValue () {
      const value = clamp(
        this.value,
        this.min,
        this.max
      )

      return percent(
        value - this.min,
        this.max - this.min
      )
    },

    thumbClass () {
      return [
        'thumb',
        { 'thumb-dragging': this.dragging }
      ]
    },

    thumbStyle () {
      /*
       * -6px so that the mouse-cursor is centered on the thumb while
       * dragging since the width of the thumb is 10px
       *
       * but also so that the left-most position of the thumb can be the
       * left edge of the rail - half of the thumb's width (and for the
       * same to be valid on the other side as well)
       */
      return { left: `calc(${this.percentValue}% - 6px)` }
    },

    trackStyle () {
      return { width: `${this.percentValue}%` }
    }
  }
}

</script>

<style scoped>

.Slider {
  padding: 8px 0;
  position: relative;
  cursor: pointer;
}

.rail {
  width: 100%;
  height: 2px;
  background: #0074D9;
  opacity: 0.2;
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
}

.track {
  height: 2px;
  background: #0074D9;
  position: absolute;
  top: 50%;
  left: 0;
  transform: translateY(-50%)
}

.thumb {
  padding: 0;
  position: absolute;
  z-index: 0;
  top: 50%;
  border-radius: 50%;
  height: 12px;
  width: 12px;
  background: white;
  border: 2px solid #0074D9;
  outline: none;
  cursor: pointer;
  transition: transform 100ms;
  transform: translateY(-50%);
}

.thumb:hover,
.thumb:focus {
  background: #0074D9;
}

.thumb::after {
  content: '';
  position: absolute;
  z-index: 0;
  top: -4px;
  left: -4px;
  height: 16px;
  width: 16px;
  border-radius: 50%;
  background: #0074D9;
  opacity: 0.2;
  transform: scale(0);
  transition: 200ms;
}

.thumb:active::after,
.thumb:focus::after,
.thumb-dragging::after {
  transform: scale(2);
}

.thumb-label {
  height: 32px;
  width: 32px;
  color: white;
  font-size: 12px;
  border-radius: 50%;
  background: #0074D9;

  display: flex;
  align-items: center;
  justify-content: center;

  position: absolute;
  top: -48px;
  left: 50%;
  opacity: 0;
  transform: translateX(-50%) scale(0);
  transition: 200ms;
  transform-origin: bottom center;
}

.thumb:hover .thumb-label,
.thumb:active .thumb-label,
.thumb:focus .thumb-label,
.thumb-dragging .thumb-label {
  opacity: 1;
  transform: translateX(-50%) scale(1);
}

</style>
